/**
 * An organization's bill as a system administrator sees it, and the two
 * billing actions that are theirs: paying by invoice (a purchase order), and
 * granting Enterprise to an organization with no subscription (`org-tier`).
 *
 * Read off the same `billing` the organization's own Billing page reads, so the
 * two never describe it differently — tier, period, a trial, a failing payment,
 * and whether it pays by invoice with which PO. The action is `invoice-billing`
 * (`enterprise.ts`): Enterprise only, refused otherwise by the Worker, and
 * offered here only where it could succeed. `org-tier` (`setGrantedTier`) is
 * offered only where there is no subscription, which is where it is allowed.
 */
import { useState } from 'react';
import type React from 'react';
import { color, font } from '../theme/tokens';
import { Badge, Card, GhostButton, Input, PrimaryButton } from '../components/primitives';
import { EnterpriseBadge } from '../components/EnterpriseBadge';
import { api, ApiError } from '../lib/api';
import { proseDay } from '../lib/format';
import type { OrgDetail } from '../lib/types';

export function OrgBilling({ detail, onChanged }: { detail: OrgDetail; onChanged: () => Promise<void> }): React.JSX.Element | null {
  const billing = detail.billing;
  const [po, setPo] = useState(billing?.poNumber ?? '');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ text: string; ok: boolean } | null>(null);
  // Money on a customer's account moves on a second press, said on the line
  // that asked rather than in a modal (redesign §47.8, proposal 35): the band
  // takes the place of the actions under the field until it is settled.
  const [confirming, setConfirming] = useState<'invoice' | 'card' | 'enterprise' | null>(null);
  if (!billing) return null;
  const enterprise = billing.tier === 'enterprise';
  const poChanged = po.trim() !== (billing.poNumber ?? '');

  const send = async (body: Record<string, unknown>): Promise<void> => {
    setBusy(true);
    setNote(null);
    try {
      const answer = await api.post<{ message: string }>('/admin/actions', body);
      setNote({ text: answer.message, ok: true });
      await onChanged();
    } catch (failure) {
      setNote({ text: failure instanceof ApiError ? failure.message : 'That did not work.', ok: false });
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  };
  const run = (enabled: boolean): Promise<void> =>
    send({ action: 'invoice-billing', orgId: detail.id, enabled, po: po.trim() || undefined });
  const setTier = (tier: 'team' | 'enterprise'): Promise<void> => send({ action: 'org-tier', orgId: detail.id, tier });

  const facts: [string, React.ReactNode][] = [
    ['Product', enterprise ? <EnterpriseBadge /> : 'Team'],
    ['Plan', billing.plan ?? '—'],
    ['Subscription', <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>{billing.stripeSubscription ?? 'none'}</span>],
    ['Trial', billing.trialStartedAt ? `started ${proseDay(billing.trialStartedAt)}` : 'never'],
    [
      'Payment',
      billing.paymentFailedAt ? <Badge tone="bad">failing since {proseDay(billing.paymentFailedAt)}</Badge> : 'ok'
    ],
    ['Pays by', billing.invoiceBilling ? `invoice, 30 days${billing.poNumber ? ` — PO ${billing.poNumber}` : ''}` : 'card']
  ];

  const band =
    confirming === 'invoice'
      ? { text: `Card → invoice, 30 days${po.trim() ? ` — PO ${po.trim()}` : ''} · from the next invoice`, verb: 'Switch to invoice' }
      : confirming === 'card'
        ? { text: 'Invoice → card · Stripe charges the card on file from the next invoice', verb: 'Switch to card' }
        : null;

  return (
    <Card style={{ marginTop: 20, maxWidth: 720 }}>
      <div style={{ fontSize: 15, fontWeight: 600 }}>Billing</div>
      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 8, marginTop: 14, fontSize: 13.5 }}>
        {facts.map(([label, value]) => (
          <div key={label} style={{ display: 'contents' }}>
            <span style={{ fontFamily: font.mono, fontSize: 12, color: color.inkFaint }}>{label}</span>
            <span>{value}</span>
          </div>
        ))}
      </div>
      {enterprise && billing.stripeSubscription && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 13.5, color: color.inkSubtle, lineHeight: 1.6 }}>
            Pay by invoice: Stripe emails each invoice to pay within 30 days, and nothing is charged automatically. The
            PO number is printed on every invoice.
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <Input
              aria-label="Purchase order number"
              placeholder="PO number (optional)"
              value={po}
              maxLength={140}
              onChange={(event) => setPo(event.target.value)}
              style={{ flex: '1 1 200px', maxWidth: 260, height: 36, fontFamily: font.mono, fontSize: 13 }}
            />
            <span style={{ flex: 1 }} />
            {!band &&
              (billing.invoiceBilling ? (
                <>
                  <GhostButton size="sm" onClick={() => setConfirming('card')} disabled={busy}>
                    Switch to card
                  </GhostButton>
                  <span title={poChanged ? undefined : 'Change the PO number first'} style={{ display: 'inline-flex' }}>
                    <PrimaryButton size="sm" onClick={() => void run(true)} disabled={busy || !poChanged}>
                      {busy ? 'Updating' : 'Update the PO'}
                    </PrimaryButton>
                  </span>
                </>
              ) : (
                <PrimaryButton size="sm" onClick={() => setConfirming('invoice')} disabled={busy}>
                  Switch to invoice
                </PrimaryButton>
              ))}
          </div>
          {band && (
            <InlineConfirm
              sentence={band.text}
              verb={band.verb}
              going="Switching"
              wait="Wait for the switch to finish"
              busy={busy}
              onCancel={() => setConfirming(null)}
              onConfirm={() => void run(confirming === 'invoice')}
            />
          )}
        </div>
      )}
      {!billing.stripeSubscription && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 13.5, color: color.inkSubtle, lineHeight: 1.6 }}>
            No subscription, so the tier is yours to set. Enterprise holds while its seats work, and a purchase later
            writes the tier its price carries.
          </div>
          {confirming === 'enterprise' ? (
            <InlineConfirm
              sentence="Team → Enterprise · while its seats work, nothing billed"
              verb="Grant Enterprise"
              going="Granting"
              wait="Wait for the grant to finish"
              busy={busy}
              onCancel={() => setConfirming(null)}
              onConfirm={() => void setTier('enterprise')}
            />
          ) : (
            <div style={{ marginTop: 12 }}>
              {enterprise ? (
                <GhostButton size="sm" onClick={() => void setTier('team')} disabled={busy}>
                  Back to Team
                </GhostButton>
              ) : (
                <PrimaryButton size="sm" onClick={() => setConfirming('enterprise')} disabled={busy}>
                  Grant Enterprise
                </PrimaryButton>
              )}
            </div>
          )}
        </div>
      )}
      {/* The result or the refusal, as a note under the card's lines (§10.6, rule 25). */}
      {note && (
        <p
          role={note.ok ? 'status' : 'alert'}
          style={{
            fontSize: 13,
            lineHeight: 1.55,
            margin: '14px 0 0',
            padding: '9px 12px',
            borderRadius: 10,
            background: note.ok ? color.okBg : color.badBg,
            color: note.ok ? color.okText : color.badText
          }}
        >
          {note.text}
        </p>
      )}
    </Card>
  );
}

/**
 * The second press, on the line that asked (bibliotheque §22): before → after
 * on the left, Cancel then the verb on the right, the gerund on the verb while
 * it goes and Cancel held at 40 % with its reason.
 */
function InlineConfirm({
  sentence,
  verb,
  going,
  wait,
  busy,
  onConfirm,
  onCancel
}: {
  sentence: string;
  verb: string;
  going: string;
  wait: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  return (
    <div
      role="group"
      aria-label="Confirm"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 10,
        marginTop: 12,
        padding: '12px 14px',
        border: `1px solid ${color.line}`,
        borderRadius: 12
      }}
    >
      <span style={{ flex: '1 1 240px', fontSize: 13, color: color.inkMuted, fontVariantNumeric: 'tabular-nums' }}>{sentence}</span>
      <span title={busy ? wait : undefined} style={{ display: 'inline-flex' }}>
        <GhostButton size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </GhostButton>
      </span>
      <PrimaryButton size="sm" onClick={onConfirm} disabled={busy}>
        {busy ? going : verb}
      </PrimaryButton>
    </div>
  );
}
